import React, { useState, useEffect } from 'react';
import axios from "../../../../axios-order";
import { Snackbar, Alert } from '@mui/material';

function Five({ handleBack, updateTableData, data, reasonsData, title, get_gstr9_report_data }) {
  const [inputValues, setInputValues] = useState(data?.map(item => item.manual_input ?? '') || []);
  const [calculatedValues, setCalculatedValues] = useState({ p: 0, r: 0 });
  const [reasonText, setReasonText] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Calculate P and R automatically
  useEffect(() => {
    if (!data || data.length < 18) return;

    const indices = {
      A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9,
      K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17
    };

    const values = inputValues.map(val => (val === '' || isNaN(parseFloat(val)) ? 0 : parseFloat(val)));

    const pValue = Number((
      values[indices.A] +
      values[indices.B] +
      values[indices.C] +
      values[indices.D] -
      values[indices.E] +
      values[indices.F] -
      values[indices.G] -
      values[indices.H] -
      values[indices.I] +
      values[indices.J] -
      values[indices.K] -
      values[indices.L] +
      values[indices.M] +
      values[indices.N] +
      values[indices.O]
    ).toFixed(2));

    const qValue = values[indices.Q] || 0;
    const rValue = Number((qValue - pValue).toFixed(2));

    setCalculatedValues({ p: pValue, r: rValue });

    const newData = [...data];
    newData[indices.P].taxable_non_taxable_value = pValue;
    newData[indices.R].taxable_non_taxable_value = rValue;

    if (typeof updateTableData === 'function') {
      updateTableData(newData);
    }
  }, [inputValues, data, updateTableData]);

  // Load reasons
  useEffect(() => {
    if (reasonsData && reasonsData.length > 0 && reasonsData[0].reasons) {
      setReasonText(reasonsData[0].reasons);
    }
  }, [reasonsData]);

  const handleSubmit = async () => {
    const indices = { A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9, K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17 };

    const payload = {
      business_entity_id: localStorage.getItem("business_entity_id"),
      gstr9c_recon_id: localStorage.getItem("gstr9c_recon_id"),
      records: {
        "5.Reconciliation of Gross Turnover": data.map((item, index) => ({
          id: item.id,
          gstr9c_recon_id: item.gstr9c_recon_id,
          business_entity_id: item.business_entity_id,
          description: item.description,
          taxable_non_taxable_value:
            index === indices.P ? calculatedValues.p :
            index === indices.R ? calculatedValues.r :
            item.taxable_non_taxable_value || 0,
          central_tax: item.central_tax || 0,
          state_tax_ut_tax: item.state_tax_ut_tax || item.state_ut_tax || 0,
          integrated_tax: item.integrated_tax || 0,
          cess: item.cess || 0,
          amount_of_itc_total: item.amount_of_itc_total || null,
          amount_of_itc_eligible: item.amount_of_itc_eligible || null,
          manual_input:
            index === indices.P ? calculatedValues.p :
            index === indices.R ? calculatedValues.r :
            inputValues[index] === '' ? null : parseFloat(inputValues[index])
        })),
        "6.Reasons for Un - Reconciled difference in Annual Gross Turnover": [
          {
            description: "Reasons for Un - Reconciled difference in Annual Gross Turnover",
            reasons: reasonText === '' ? null : reasonText
          }
        ]
      }
    };

    try {
      const response = await axios.post("/gstr9c_manual_data_update", payload);
      setSnackbar({
        open: true,
        message: response.data.message || 'Values updated successfully.',
        severity: response.data.status ? 'success' : 'error',
      });
      if (response.data.status && typeof get_gstr9_report_data === 'function') {
        await get_gstr9_report_data();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while submitting data.',
        severity: 'error',
      });
    }
  };

  // Unified input change handler with clean negative/decimal support
  const handleManualInputChange = (index, value) => {
    if (index === 15 || index === 17) return; // P and R are read-only

    let clean = value.replace('₹', '').trim();

    if (clean === '') {
      const newInputValues = [...inputValues];
      newInputValues[index] = '';
      setInputValues(newInputValues);

      const newData = [...data];
      newData[index].manual_input = null;
      updateTableData?.(newData);
      return;
    }

    // Allow only one minus at the start
    if (clean.includes('-')) {
      clean = '-' + clean.replace(/-/g, '');
    }

    // Validate format: optional -, digits, optional decimal with up to 2 places
    if (/^-?\d*\.?\d{0,2}$/.test(clean)) {
      const newInputValues = [...inputValues];
      newInputValues[index] = clean;
      setInputValues(newInputValues);

      const parsed = parseFloat(clean);
      const newData = [...data];
      newData[index].manual_input = isNaN(parsed) ? null : parsed;
      updateTableData?.(newData);
    }
  };

  // Unified keydown handler - fixes minus sign issue
  const handleKeyDown = (e) => {
    const index = parseInt(e.target.dataset.index);
    if (index === 15 || index === 17) {
      e.preventDefault();
      return;
    }

    const allowedKeys = ['0','1','2','3','4','5','6','7','8','9','Backspace','Delete','ArrowLeft','ArrowRight','Tab','.'];
    if (allowedKeys.includes(e.key)) return;

    const input = e.target;
    const rawValue = input.value;
    const cleanValue = rawValue.replace('₹', '').trim();
    const cursorPos = input.selectionStart;

    // Allow only one decimal point
    if (e.key === '.') {
      if (cleanValue.includes('.')) e.preventDefault();
      return;
    }

    // Allow minus only at start (position 0 or 1) and only once
    if (e.key === '-') {
      const isAtStart = cursorPos === 0 || cursorPos === 1;
      const hasMinus = cleanValue.startsWith('-');

      if (isAtStart && !hasMinus) {
        return; // allow minus
      } else {
        e.preventDefault();
      }
      return;
    }

    e.preventDefault();
  };

  // Focus handler to place cursor after ₹
  const handleFocus = (e) => {
    if (e.target.readOnly) return;

    e.target.style.borderColor = '#88d8a3';
    e.target.style.boxShadow = '0 0 0 2px rgba(136, 216, 163, 0.1)';

    const val = e.target.value;
    const pos = val.startsWith('₹') ? 1 : 0;
    setTimeout(() => e.target.setSelectionRange(pos, pos), 0);
  };

  const handleBlur = (e) => {
    if (e.target.readOnly) return;

    e.target.style.borderColor = '#e2e8f0';
    e.target.style.boxShadow = 'none';
  };

  const hiddenColumnsDescriptions = [
    'G. Turnover from April 2017 to June 2017',
  ];

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '30px',
        width: '100%',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={handleBack}
          style={{
            marginBottom: '20px',
            padding: '8px 16px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#64748b'
          }}
        >
          Back
        </button>

        <h2 style={{
          marginBottom: '20px',
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '700',
          textAlign: 'center'
        }}>
          {title || 'Table 5 - Reconciliation of Gross Turnover'}
        </h2>

        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 2fr 1fr',
          gap: '10px',
          fontWeight: '600',
          color: '#475569',
          padding: '15px 10px',
          borderBottom: '2px solid #e2e8f0',
          fontSize: '14px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          marginBottom: '10px',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <span style={{ textAlign: 'center' }}>S.No.</span>
          <span>Description</span>
          <span style={{ textAlign: 'center' }}>Taxable/Non-Taxable Value</span>
        </div>

        {/* Table Body */}
        <div style={{
          maxHeight: 'calc(100vh - 450px)',
          overflowY: 'auto',
          marginTop: '10px'
        }}>
          {data && data.length > 0 ? data.map((item, index) => {
            const getRowColor = (i) => ['#e0e7ff', '#e9d5ff', '#f3e8ff', '#ede9fe', '#ddd6fe'][i % 5];

            const displayValue = index === 15 ? calculatedValues.p :
                                index === 17 ? calculatedValues.r :
                                inputValues[index];

            const isReadOnly = index === 15 || index === 17;
            const isHidden = hiddenColumnsDescriptions.some(desc =>
              item.description && desc.toLowerCase().trim() === item.description.toLowerCase().trim()
            );

            return (
              <div key={index} style={{
                display: 'grid',
                gridTemplateColumns: '60px 2fr 1fr',
                gap: '10px',
                alignItems: 'center',
                padding: '10px',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                borderRadius: '6px',
                marginBottom: '2px',
                minHeight: '40px'
              }}>
                <span style={{
                  color: '#1e293b',
                  fontSize: '13px',
                  fontWeight: '600',
                  textAlign: 'center',
                  padding: '6px',
                  backgroundColor: getRowColor(index),
                  borderRadius: '6px'
                }}>
                  {index + 1}
                </span>

                <span style={{
                  color: '#334155',
                  fontSize: '13px',
                  padding: '6px 10px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '6px',
                  wordBreak: 'break-word'
                }}>
                  {item.description || 'N/A'}
                </span>

                {isHidden ? (
                  <div style={{
                    color: '#1e293b',
                    fontWeight: '500',
                    fontSize: '13px',
                    textAlign: 'right',
                    padding: '6px',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}></div>
                ) : (
                  <input
                    type="text"
                    value={displayValue != null && displayValue !== '' ? `₹${displayValue}` : ''}
                    onChange={(e) => handleManualInputChange(index, e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    readOnly={isReadOnly}
                    data-index={index}
                    style={{
                      color: '#1e293b',
                      fontWeight: '500',
                      fontSize: '13px',
                      textAlign: 'right',
                      padding: '6px',
                      backgroundColor: isReadOnly ? '#f0f0f0' : '#ecfdf5',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      outline: 'none',
                      width: '100%',
                      cursor: isReadOnly ? 'not-allowed' : 'text',
                      minHeight: '40px'
                    }}
                  />
                )}
              </div>
            );
          }) : (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
              No data available for this section.
            </div>
          )}
        </div>

        {/* Footer */}
        {data && data.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            borderTop: '2px solid #e2e8f0'
          }}>
            <p style={{ margin: '0', fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
              Total Records: {data.length}
            </p>
          </div>
        )}

        {/* Reasons Banner */}
        <div style={{
          marginTop: '30px',
          display: 'flex',
          alignItems: 'center',
          height: '80px',
          gap: '0'
        }}>
          <div style={{
            position: 'relative',
            width: '50%',
            height: '40px',
            background: 'linear-gradient(135deg, rgb(187, 63, 25) 0%, rgb(157, 58, 58) 50%, rgb(127, 205, 205) 100%)',
            clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(168, 230, 207, 0.3)',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              right: '10px',
              width: '40px',
              height: '100%',
              background: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'%3E%3Cdefs%3E%3Cpattern id=\'pattern2\' x=\'0\' y=\'0\' width=\'15\' height=\'15\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'7.5\' cy=\'7.5\' r=\'1.5\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23pattern2)\'/%3E%3C/svg%3E")',
              zIndex: 1
            }}></div>
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              zIndex: 2,
              marginRight: '20px'
            }}>
              6. Reasons for Un - Reconciled difference in Annual Gross Turnover
            </div>
          </div>

          <div style={{
            width: '50%',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            backgroundColor: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderLeft: 'none',
            gap: '10px'
          }}>
            <textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Enter additional notes or remarks..."
              style={{
                flex: 1,
                padding: '10px 15px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
                resize: 'none',
                height: '60px'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#88d8a3';
                e.target.style.boxShadow = '0 0 0 2px rgba(136, 216, 163, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              onClick={handleSubmit}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #ffd3a5 0%, #fd9853 50%, #e8a87c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 211, 165, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Submit
            </button>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default Five;